import type {
  DirectedCastDescription,
  DirectedCastRole,
  DirectedScript,
  DirectedStory,
  DirectedStyle,
  DirectedVideoHistoryResponse,
  DirectedVideoListResponse,
  DirectedVideoPriceResponse,
  DirectedVideoResponse,
} from 'fym-shared/src/music24/directedVideoApi';
import { fymAPI, VIDEO_CREDIT_TAGS } from '../../redux/api/fymAPI';

/**
 * The routes of the directed video (spec `2026-10-17-music24-directed-video-design.md` §10), under
 * `/accounts/:accountId/music-video/:catalogItemUuid/directed`. The shapes are the shared contract
 * `fym-shared/src/music24/directedVideoApi.ts`.
 *
 * Tags: `DirectedVideoList` (id = the catalog item uuid: the directed videos of the song) and
 * `DirectedVideo` (id = the directed video uuid: one video and its price). The realtime event
 * `musicVideo.directed.updated` invalidates both (`realtimeEventTags.ts`), so the wizard reads the
 * video again when the director or the make moves on. Every write answers the video; the write
 * invalidates its tags, and the screen shows the answer of the read that follows.
 */

interface SongArgs {
  accountId: number;
  catalogItemUuid: string;
}

export interface DirectedVideoArgs extends SongArgs {
  uuid: string;
}

interface CastArgs extends DirectedVideoArgs {
  castId: string;
}

export type AddDirectedCastOptions = DirectedVideoArgs & {
  fileId: number;
  role: DirectedCastRole;
  label: string;
  consent: boolean;
};
export type UpdateDirectedCastOptions = CastArgs & {
  role?: DirectedCastRole;
  label?: string;
  consent?: boolean;
  /** The artist's correction of what the AI read in the picture; `null` clears it. */
  description?: DirectedCastDescription | null;
};
export type MakeDirectedCastPictureOptions = DirectedVideoArgs & {
  role: DirectedCastRole;
  label: string;
  description: string;
  /** The failed pending picture that this request tries again: it goes when the new one starts. */
  replacesPictureId?: string;
};
export type DirectorRunOptions = DirectedVideoArgs & { note?: string };
export type RewriteDirectedShotOptions = DirectorRunOptions & { shotId: string };
export type PickDirectedStoryOptions = DirectedVideoArgs & { story: DirectedStory };
export type PickDirectedStyleOptions = DirectedVideoArgs & { style: DirectedStyle };
export type SaveDirectedScriptOptions = DirectedVideoArgs & { scriptRevision: number; script: DirectedScript };
export type ConfirmDirectedVideoOptions = DirectedVideoArgs & { scriptRevision: number };

function songUrl({ accountId, catalogItemUuid }: SongArgs): string {
  return `accounts/${accountId}/music-video/${encodeURIComponent(catalogItemUuid)}/directed`;
}

function videoUrl(args: DirectedVideoArgs): string {
  return `${songUrl(args)}/${encodeURIComponent(args.uuid)}`;
}

function castUrl(args: CastArgs): string {
  return `${videoUrl(args)}/cast/${encodeURIComponent(args.castId)}`;
}

const JSON_HEADERS = { 'content-type': 'application/json' };

/** The tags that one write of a video changes: the video (and its price) and the list of its song. */
function videoTags({ catalogItemUuid, uuid }: DirectedVideoArgs) {
  return [
    { type: 'DirectedVideo' as const, id: uuid },
    { type: 'DirectedVideoList' as const, id: catalogItemUuid },
  ];
}

/** The body of a director run: the note only when the artist wrote one. */
function noteBody(note: string | undefined): string {
  return JSON.stringify(note === undefined || note.trim() === '' ? {} : { note });
}

export const directedVideoApi = fymAPI
  .enhanceEndpoints({ addTagTypes: ['DirectedVideo', 'DirectedVideoList', 'MusicVideoEditor'] })
  .injectEndpoints({
    endpoints: (builder) => ({
      // `GET /` → the directed videos of the song, newest first.
      getDirectedVideos: builder.query<DirectedVideoListResponse, SongArgs>({
        query: (args) => ({ url: songUrl(args), method: 'GET' }),
        providesTags: (response, error, { catalogItemUuid }) => [
          { type: 'DirectedVideoList' as const, id: catalogItemUuid },
        ],
      }),
      // `POST /` → a new video in the status `cast`.
      createDirectedVideo: builder.mutation<DirectedVideoResponse, SongArgs>({
        query: (args) => ({ url: songUrl(args), method: 'POST', body: '{}', headers: JSON_HEADERS }),
        invalidatesTags: (response, error, { catalogItemUuid }) => [
          { type: 'DirectedVideoList' as const, id: catalogItemUuid },
        ],
      }),
      getDirectedVideo: builder.query<DirectedVideoResponse, DirectedVideoArgs>({
        query: (args) => ({ url: videoUrl(args), method: 'GET' }),
        providesTags: (response, error, { uuid }) => [{ type: 'DirectedVideo' as const, id: uuid }],
      }),
      // `GET /:uuid/history` → the rounds of the director and the picks ("How it was made", l1).
      getDirectedVideoHistory: builder.query<DirectedVideoHistoryResponse, DirectedVideoArgs>({
        query: (args) => ({ url: `${videoUrl(args)}/history`, method: 'GET' }),
        providesTags: (response, error, { uuid }) => [{ type: 'DirectedVideo' as const, id: uuid }],
      }),
      // `DELETE /:uuid` → `cancelled` (only before `making`).
      cancelDirectedVideo: builder.mutation<void, DirectedVideoArgs>({
        query: (args) => ({ url: videoUrl(args), method: 'DELETE' }),
        invalidatesTags: (response, error, args) => videoTags(args),
      }),

      // ------------------------------------------ the cast ------------------------------------------
      addDirectedCast: builder.mutation<DirectedVideoResponse, AddDirectedCastOptions>({
        query: ({ fileId, role, label, consent, ...args }) => ({
          url: `${videoUrl(args)}/cast`,
          method: 'POST',
          body: JSON.stringify({ fileId, role, label, consent }),
          headers: JSON_HEADERS,
        }),
        invalidatesTags: (response, error, args) => videoTags(args),
      }),
      // Owner request 2026-10-19: the artist picture of the account goes into the cast as "me".
      addDirectedCastFromArtistPicture: builder.mutation<DirectedVideoResponse, DirectedVideoArgs>({
        query: (args) => ({
          url: `${videoUrl(args)}/cast/artist-picture`,
          method: 'POST',
          body: JSON.stringify({}),
          headers: JSON_HEADERS,
        }),
        invalidatesTags: (response, error, args) => videoTags(args),
      }),
      updateDirectedCast: builder.mutation<DirectedVideoResponse, UpdateDirectedCastOptions>({
        query: ({ role, label, consent, description, ...args }) => ({
          url: castUrl(args),
          method: 'PATCH',
          body: JSON.stringify({ role, label, consent, description }),
          headers: JSON_HEADERS,
        }),
        invalidatesTags: (response, error, args) => videoTags(args),
      }),
      removeDirectedCast: builder.mutation<DirectedVideoResponse, CastArgs>({
        query: (args) => ({ url: castUrl(args), method: 'DELETE' }),
        invalidatesTags: (response, error, args) => videoTags(args),
      }),
      // 202: `GenerateCastPictureJob` makes the picture; it shows in `castPicturesPending` until then.
      makeDirectedCastPicture: builder.mutation<DirectedVideoResponse, MakeDirectedCastPictureOptions>({
        query: ({ role, label, description, replacesPictureId, ...args }) => ({
          url: `${videoUrl(args)}/cast/picture`,
          method: 'POST',
          body: JSON.stringify({
            role,
            label,
            description,
            ...(replacesPictureId === undefined ? {} : { replacesPictureId }),
          }),
          headers: JSON_HEADERS,
        }),
        invalidatesTags: (response, error, args) => videoTags(args),
      }),

      // ------------------------------------------ the director ------------------------------------------
      // Each run answers 202 with `directorState: 'working'`; the realtime event says when it ends.
      writeDirectedStories: builder.mutation<DirectedVideoResponse, DirectorRunOptions>({
        query: ({ note, ...args }) => ({
          url: `${videoUrl(args)}/stories`,
          method: 'POST',
          body: noteBody(note),
          headers: JSON_HEADERS,
        }),
        invalidatesTags: (response, error, args) => videoTags(args),
      }),
      pickDirectedStory: builder.mutation<DirectedVideoResponse, PickDirectedStoryOptions>({
        query: ({ story, ...args }) => ({
          url: `${videoUrl(args)}/story`,
          method: 'PUT',
          body: JSON.stringify(story),
          headers: JSON_HEADERS,
        }),
        invalidatesTags: (response, error, args) => videoTags(args),
      }),
      writeDirectedStyles: builder.mutation<DirectedVideoResponse, DirectorRunOptions>({
        query: ({ note, ...args }) => ({
          url: `${videoUrl(args)}/styles`,
          method: 'POST',
          body: noteBody(note),
          headers: JSON_HEADERS,
        }),
        invalidatesTags: (response, error, args) => videoTags(args),
      }),
      pickDirectedStyle: builder.mutation<DirectedVideoResponse, PickDirectedStyleOptions>({
        query: ({ style, ...args }) => ({
          url: `${videoUrl(args)}/style`,
          method: 'PUT',
          body: JSON.stringify(style),
          headers: JSON_HEADERS,
        }),
        invalidatesTags: (response, error, args) => videoTags(args),
      }),
      writeDirectedScript: builder.mutation<DirectedVideoResponse, DirectorRunOptions>({
        query: ({ note, ...args }) => ({
          url: `${videoUrl(args)}/script`,
          method: 'POST',
          body: noteBody(note),
          headers: JSON_HEADERS,
        }),
        invalidatesTags: (response, error, args) => videoTags(args),
      }),
      // The artist's edit. A stale `scriptRevision` answers 409 `M24_DIRECTED_STALE`.
      saveDirectedScript: builder.mutation<DirectedVideoResponse, SaveDirectedScriptOptions>({
        query: ({ scriptRevision, script, ...args }) => ({
          url: `${videoUrl(args)}/script`,
          method: 'PUT',
          body: JSON.stringify({ scriptRevision, script }),
          headers: JSON_HEADERS,
        }),
        invalidatesTags: (response, error, args) => videoTags(args),
      }),
      rewriteDirectedShot: builder.mutation<DirectedVideoResponse, RewriteDirectedShotOptions>({
        query: ({ note, shotId, ...args }) => ({
          url: `${videoUrl(args)}/shots/${encodeURIComponent(shotId)}/rewrite`,
          method: 'POST',
          body: noteBody(note),
          headers: JSON_HEADERS,
        }),
        invalidatesTags: (response, error, args) => videoTags(args),
      }),

      // ------------------------------------------ price and make ------------------------------------------
      // The `DirectedVideo` tag: a script edit and the realtime event read the price again.
      getDirectedVideoPrice: builder.query<DirectedVideoPriceResponse, DirectedVideoArgs>({
        query: (args) => ({ url: `${videoUrl(args)}/price`, method: 'GET' }),
        providesTags: (response, error, { uuid }) => [{ type: 'DirectedVideo' as const, id: uuid }],
      }),
      // The charge. A stale revision answers 409 `M24_DIRECTED_STALE` and charges nothing.
      confirmDirectedVideo: builder.mutation<DirectedVideoResponse, ConfirmDirectedVideoOptions>({
        query: ({ scriptRevision, ...args }) => ({
          url: `${videoUrl(args)}/confirm`,
          method: 'POST',
          body: JSON.stringify({ scriptRevision }),
          headers: JSON_HEADERS,
        }),
        invalidatesTags: (response, error, args) => [
          ...videoTags(args),
          { type: 'MusicVideoEditor' as const, id: args.catalogItemUuid },
          ...VIDEO_CREDIT_TAGS,
        ],
      }),
    }),
  });

export const {
  useGetDirectedVideosQuery,
  useCreateDirectedVideoMutation,
  useGetDirectedVideoQuery,
  useGetDirectedVideoHistoryQuery,
  useCancelDirectedVideoMutation,
  useAddDirectedCastMutation,
  useAddDirectedCastFromArtistPictureMutation,
  useUpdateDirectedCastMutation,
  useRemoveDirectedCastMutation,
  useMakeDirectedCastPictureMutation,
  useWriteDirectedStoriesMutation,
  usePickDirectedStoryMutation,
  useWriteDirectedStylesMutation,
  usePickDirectedStyleMutation,
  useWriteDirectedScriptMutation,
  useSaveDirectedScriptMutation,
  useRewriteDirectedShotMutation,
  useGetDirectedVideoPriceQuery,
  useLazyGetDirectedVideoPriceQuery,
  useConfirmDirectedVideoMutation,
} = directedVideoApi;
